import { useState, useCallback, useEffect, useRef } from 'react';
import { AdaptiveAPIClient } from '@utils/AdaptiveAPIClient.js';

/**
 * useAdaptiveFlow Hook
 * 
 * Complete hook for managing adaptive learning flow
 * Handles:
 * - Session initialization and resumption
 * - Content/question delivery
 * - Answer submission
 * - Remediation context
 * - Spaced review injection
 * - Session state management
 * 
 * Usage:
 * ```jsx
 * function AdaptiveSessionPage() {
 *   const { 
 *     step, 
 *     sessionId,
 *     loading, 
 *     error,
 *     sessionState,
 *     fetchNextStep,
 *     submitAnswer,
 *     completeSession
 *   } = useAdaptiveFlow(userId, courseId);
 * 
 *   if (loading) return <div>Loading...</div>;
 *   if (error) return <div>Error: {error}</div>;
 * 
 *   if (step?.type === 'question_group') {
 *     return <QuestionGroup questions={step.questions} onSubmit={submitAnswer} />;
 *   }
 *   
 *   if (step?.type === 'content') {
 *     return <ContentDisplay content={step.content} onNext={fetchNextStep} />;
 *   }
 *   
 *   // Handle other step types...
 * }
 * ```
 */
export function useAdaptiveFlow(userId, courseId, options = {}) {
  const {
    baseURL = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_ADAPTIVE_API_BASE_URL || '/api/v1/adaptive',
    autoStart = true,
    autoAdvanceOnSubmit = true,
    onSessionStart = null,
    onSessionEnd = null,
  } = options;

  // State management
  const [sessionId, setSessionId] = useState(null);
  const [step, setStep] = useState(null);
  const [loading, setLoading] = useState(autoStart);
  const [error, setError] = useState(null);
  const [sessionState, setSessionState] = useState({
    questionsAnswered: 0,
    correctAnswers: 0,
    currentSection: null,
    nextGroupSize: 5,
  });
  const [isComplete, setIsComplete] = useState(false);
  const [currentSectionId, setCurrentSectionId] = useState(null);
  const [nextStepPrefetched, setNextStepPrefetched] = useState(null);
  const [prefetchInProgress, setPrefetchInProgress] = useState(false);

  // Refs for tracking
  const apiClientRef = useRef(new AdaptiveAPIClient(baseURL));
  const inititalizedRef = useRef(false);
  const answerHistoryRef = useRef([]);
  const lastPrefetchTime = useRef(0);
  const traceCounterRef = useRef(0);
  const previousStepTypeRef = useRef(null);

  const logFlow = useCallback((event, payload = {}) => {
    if (import.meta.env.VITE_ADAPTIVE_TRACE === '0') return;
    traceCounterRef.current += 1;
    console.log(`[useAdaptiveFlow.${event}]`, {
      trace: traceCounterRef.current,
      userId,
      courseId,
      sessionId,
      currentSectionId,
      stepType: step?.type || null,
      ...payload,
    });
  }, [userId, courseId, sessionId, currentSectionId, step?.type]);

  /**
   * Initialize or resume session
   */
  const initializeSession = useCallback(async () => {
    if (!userId || !courseId) {
      setError('Missing userId or courseId');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      logFlow('initialize.start');

      const response = await apiClientRef.current.startSession(userId, courseId, true);

      if (!response.success) {
        throw new Error(response.error || 'Failed to initialize session');
      }

      setSessionId(response.sessionId);
      setCurrentSectionId(response.currentSection?.id);
      logFlow('initialize.session_started', {
        startedSessionId: response.sessionId,
        status: response.status,
        responseSectionId: response.currentSection?.id || null,
      });

      if (onSessionStart) {
        onSessionStart({
          sessionId: response.sessionId,
          status: response.status,
        });
      }

      // Get initial step
      const initialStep = await apiClientRef.current.getNextStep(
        userId,
        courseId,
        response.currentSection?.id,
        response.sessionId
      );

      if (!initialStep.success) {
        throw new Error(initialStep.error || 'Failed to get initial adaptive step');
      }

      const normalizedInitialStep = {
        type: initialStep.type,
        nextAction: initialStep.nextAction,
        message: initialStep.message,
      };

      if (initialStep.type === 'content') {
        normalizedInitialStep.content = initialStep.content;
      } else if (initialStep.type === 'question_group') {
        normalizedInitialStep.questions = initialStep.questions;
        normalizedInitialStep.groupSize = initialStep.groupSize;
      } else if (initialStep.type === 'remediation_content') {
        normalizedInitialStep.remediationSection = initialStep.remediationSection;
        normalizedInitialStep.context = initialStep.context;
        normalizedInitialStep.isRemediationContext = true;
      } else if (initialStep.type === 'spaced_review') {
        normalizedInitialStep.reviewSection = initialStep.reviewSection;
        normalizedInitialStep.questions = initialStep.questions;
        normalizedInitialStep.resumeToSectionIndex = initialStep.resumeToSectionIndex;
        normalizedInitialStep.isSpacedReviewContext = true;
      } else if (initialStep.type === 'section_complete') {
        normalizedInitialStep.completedSectionId = initialStep.completedSectionId;
      }

      logFlow('initialize.initial_step', {
        initialType: initialStep.type,
        nextAction: initialStep.nextAction,
        groupSize: initialStep.groupSize || 0,
        reinforcementCount: initialStep.reinforcementCount || 0,
      });

      setStep(normalizedInitialStep);
      setSessionState(prev => ({
        ...prev,
        questionsAnswered: initialStep.performance?.questionsAnswered || prev.questionsAnswered,
        correctAnswers: initialStep.performance?.correctAnswers || prev.correctAnswers,
      }));
    } catch (err) {
      console.error('[useAdaptiveFlow] Failed to initialize:', err);
      logFlow('initialize.error', { message: err.message || 'unknown' });
      setError(err.message || 'Failed to initialize session');
    } finally {
      setLoading(false);
    }
  }, [userId, courseId, onSessionStart, logFlow]);

  /**
   * Normalize API response to step object
   */
  const normalizeStep = useCallback((response) => {
    const normalizedStep = {
      type: response.type,
      nextAction: response.nextAction,
      message: response.message,
    };

    // Add type-specific data
    if (response.type === 'content') {
      normalizedStep.content = response.content;
    } else if (response.type === 'question_group') {
      normalizedStep.questions = response.questions;
      normalizedStep.groupSize = response.groupSize;
      normalizedStep.reinforcementCount = response.reinforcementCount || 0;
    } else if (response.type === 'remediation_content') {
      normalizedStep.remediationSection = response.remediationSection;
      normalizedStep.context = response.context;
      normalizedStep.isRemediationContext = true;
    } else if (response.type === 'spaced_review') {
      normalizedStep.reviewSection = response.reviewSection;
      normalizedStep.questions = response.questions;
      normalizedStep.resumeToSectionIndex = response.resumeToSectionIndex;
      normalizedStep.isSpacedReviewContext = true;
    } else if (response.type === 'section_complete') {
      normalizedStep.completedSectionId = response.completedSectionId;
      normalizedStep.nextSectionId = response.nextSectionId;
      normalizedStep.isLastSection = response.isLastSection;
    }

    logFlow('normalize.step', {
      responseType: response.type,
      nextAction: response.nextAction,
      groupSize: response.groupSize || 0,
      reinforcementCount: response.reinforcementCount || 0,
      hasNextSectionId: !!response.nextSectionId,
    });

    return normalizedStep;
  }, [logFlow]);

  /**
   * Get next adaptive step
   */
  const getNextStep = useCallback(async (sid = sessionId, sectionId = currentSectionId) => {
    if (!sid || !sectionId) {
      setError('Missing sessionId or sectionId');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      logFlow('next.request', { sid, sectionId });

      const response = await apiClientRef.current.getNextStep(
        userId,
        courseId,
        sectionId,
        sid
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to get next step');
      }

      const normalizedStep = normalizeStep(response);
      setStep(normalizedStep);
      logFlow('next.response', {
        responseType: response.type,
        nextAction: response.nextAction,
        responseGroupSize: response.groupSize || 0,
        responseReinforcementCount: response.reinforcementCount || 0,
        isLastSection: !!response.isLastSection,
        nextSectionId: response.nextSectionId || null,
      });

      // Update session state
      setSessionState(prev => ({
        ...prev,
        questionsAnswered: response.performance?.questionsAnswered || prev.questionsAnswered,
        correctAnswers: response.performance?.correctAnswers || prev.correctAnswers,
        nextGroupSize: response.groupSize || response.performance?.nextGroupSize || prev.nextGroupSize,
      }));
    } catch (err) {
      console.error('[useAdaptiveFlow] Failed to get next step:', err);
      logFlow('next.error', { message: err.message || 'unknown', sid, sectionId });
      setError(err.message || 'Failed to get next step');
    } finally {
      setLoading(false);
    }
  }, [userId, courseId, sessionId, currentSectionId, normalizeStep, logFlow]);

  /**
   * Prefetch next step in background (non-blocking)
   * Speeds up question transitions by 500-1000ms
   * Includes deduplication and throttling
   */
  const prefetchNextStep = useCallback(async (sid = sessionId, sectionId = currentSectionId) => {
    if (!sid || !sectionId) return;
    
    // Prevent duplicate prefetch requests
    if (prefetchInProgress) {
      console.debug('[useAdaptiveFlow] Prefetch already in progress, skipping');
      logFlow('prefetch.skip_in_progress', { sid, sectionId });
      return;
    }

    // Throttle prefetch requests (min 500ms between calls)
    const now = Date.now();
    if (now - lastPrefetchTime.current < 500) {
      console.debug('[useAdaptiveFlow] Prefetch throttled');
      logFlow('prefetch.skip_throttled', {
        sid,
        sectionId,
        deltaMs: now - lastPrefetchTime.current,
      });
      return;
    }
    
    setPrefetchInProgress(true);
    lastPrefetchTime.current = now;
    
    try {
      const startTime = performance.now();
      logFlow('prefetch.start', { sid, sectionId });
      const response = await apiClientRef.current.getNextStep(
        userId,
        courseId,
        sectionId,
        sid
      );

      if (response.success) {
        const duration = performance.now() - startTime;
        console.debug(`[useAdaptiveFlow] Prefetch completed in ${duration.toFixed(0)}ms`);
        logFlow('prefetch.success', {
          sid,
          sectionId,
          durationMs: Number(duration.toFixed(0)),
          responseType: response.type,
        });
        setNextStepPrefetched(response);
      }
    } catch (err) {
      // Silent fail - prefetch is non-critical
      console.debug('[useAdaptiveFlow] Prefetch failed (non-blocking):', err);
      logFlow('prefetch.error', { sid, sectionId, message: err.message || 'unknown' });
    } finally {
      setPrefetchInProgress(false);
    }
  }, [userId, courseId, sessionId, currentSectionId, prefetchInProgress, logFlow]);

  /**
   * Submit answer
   */
  const submitAnswer = useCallback(async (answerData) => {
    if (!sessionId) {
      setError('No active session');
      return null;
    }

    const submitStartTime = performance.now();

    try {
      setError(null);

      const isLastQuestionInSet = !!answerData?.isLastQuestion;
      logFlow('submit.start', {
        questionId: answerData?.questionId,
        isCorrect: answerData?.isCorrect,
        isLastQuestionInSet,
      });

      // Track answer in history immediately (optimistic)
      answerHistoryRef.current.push({
        questionId: answerData.questionId,
        isCorrect: answerData.isCorrect,
        timestamp: new Date().toISOString(),
      });

      // Update session state (optimistic)
      setSessionState(prev => ({
        ...prev,
        questionsAnswered: (prev.questionsAnswered || 0) + 1,
        correctAnswers: (prev.correctAnswers || 0) + (answerData.isCorrect ? 1 : 0),
      }));

      const normalizedAnswerData = {
        ...answerData,
        selectedAnswer: answerData?.selectedAnswer ?? answerData?.userAnswer ?? null,
      };

      // Submit answer
      const submitPromise = apiClientRef.current.submitAnswer({
        userId,
        courseId,
        sessionId,
        ...normalizedAnswerData,
      }).then(response => {
        if (!response.success) {
          throw new Error(response.error || 'Failed to submit answer');
        }

        // Update session state with server data
        setSessionState(prev => ({
          ...prev,
          nextGroupSize: response.performance?.nextGroupSize || prev.nextGroupSize,
        }));

        const submitDuration = performance.now() - submitStartTime;
        console.log('[useAdaptiveFlow] Answer submitted in background:', {
          isCorrect: answerData.isCorrect,
          duration: `${submitDuration.toFixed(0)}ms`,
          isLastQuestionInSet,
        });
        logFlow('submit.success', {
          questionId: answerData?.questionId,
          isCorrect: answerData?.isCorrect,
          isLastQuestionInSet,
          durationMs: Number(submitDuration.toFixed(0)),
          nextGroupSize: response.performance?.nextGroupSize || null,
        });

        return response;
      }).catch(err => {
        console.error('[useAdaptiveFlow] Background submit failed:', err);
        logFlow('submit.error_background', {
          questionId: answerData?.questionId,
          message: err.message || 'unknown',
        });
        // Don't set error here as user has already moved on
      });

      // Advance only after completing the question set (last question)
      if (autoAdvanceOnSubmit && isLastQuestionInSet) {
        // Clear any stale prefetched step captured before all answers were submitted
        setNextStepPrefetched(null);
        logFlow('submit.auto_advance', {
          questionId: answerData?.questionId,
          nextSectionProbe: currentSectionId,
        });

        // CRITICAL: wait for submit persistence before asking /next.
        // /next progression relies on latest session history updates.
        const committedResponse = await submitPromise;
        logFlow('submit.last_question_committed', {
          questionId: answerData?.questionId,
          committed: !!committedResponse,
        });

        await getNextStep(sessionId, currentSectionId);

        return committedResponse || { success: true };
      }

      // Wait for submit to complete before returning
      const response = await submitPromise;
      return response || { success: true };
    } catch (err) {
      console.error('[useAdaptiveFlow] Failed to submit answer:', err);
      logFlow('submit.error', {
        questionId: answerData?.questionId,
        message: err.message || 'unknown',
      });
      setError(err.message || 'Failed to submit answer');
      return null;
    }
  }, [userId, courseId, sessionId, currentSectionId, getNextStep, autoAdvanceOnSubmit, logFlow]);

  /**
   * Move to next question/section
   */
  const fetchNextStep = useCallback(async () => {
    if (!sessionId || !currentSectionId) {
      setError('Missing session or section');
      return;
    }

    logFlow('fetch_next.manual', { sessionId, currentSectionId });
    await getNextStep(sessionId, currentSectionId);
  }, [sessionId, currentSectionId, getNextStep, logFlow]);

  /**
   * Move to specific section (for remediation resume)
   */
  const resumeToSection = useCallback(async (sectionId) => {
    if (!sessionId) {
      setError('No active session');
      return;
    }

    setCurrentSectionId(sectionId);
    logFlow('resume_to_section', { targetSectionId: sectionId });
    await getNextStep(sessionId, sectionId);
  }, [sessionId, getNextStep, logFlow]);

  /**
   * Get session summary
   */
  const getSessionSummary = useCallback(async () => {
    if (!sessionId) {
      setError('No active session');
      return null;
    }

    try {
      logFlow('summary.request');
      return await apiClientRef.current.getSessionSummary(userId, courseId, sessionId);
    } catch (err) {
      console.error('[useAdaptiveFlow] Failed to get session summary:', err);
      logFlow('summary.error', { message: err.message || 'unknown' });
      setError(err.message);
      return null;
    }
  }, [userId, courseId, sessionId, logFlow]);

  /**
   * Complete session
   */
  const completeSession = useCallback(async () => {
    if (!sessionId) {
      setError('No active session');
      return null;
    }

    try {
      setLoading(true);
      const response = await apiClientRef.current.completeSession(userId, courseId, sessionId);

      if (response.success) {
        setIsComplete(true);
        logFlow('complete.success', { sessionId, stats: response.stats || null });

        if (onSessionEnd) {
          onSessionEnd({
            sessionId,
            stats: response.stats,
          });
        }
      }

      return response;
    } catch (err) {
      console.error('[useAdaptiveFlow] Failed to complete session:', err);
      logFlow('complete.error', { message: err.message || 'unknown' });
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId, courseId, sessionId, onSessionEnd, logFlow]);

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Initialize on mount
   */
  useEffect(() => {
    apiClientRef.current = new AdaptiveAPIClient(baseURL);
  }, [baseURL]);

  useEffect(() => {
    if (!inititalizedRef.current && autoStart && userId && courseId) {
      inititalizedRef.current = true;
      initializeSession();
    }
  }, [autoStart, userId, courseId, initializeSession]);

  /**
   * Auto-advance on section_complete: move to the next section or mark course done
   */
  useEffect(() => {
    if (step?.type !== 'section_complete') return;

    logFlow('effect.section_complete', {
      isLastSection: !!step.isLastSection,
      nextSectionId: step.nextSectionId || null,
      completedSectionId: step.completedSectionId || null,
    });

    if (!autoAdvanceOnSubmit) {
      logFlow('effect.section_complete.manual_mode', {
        isLastSection: !!step.isLastSection,
        nextSectionId: step.nextSectionId || null,
      });

      if (step.isLastSection || !step.nextSectionId) {
        setIsComplete(true);
        if (onSessionEnd) onSessionEnd({ sessionId, stats: sessionState });
      }
      return;
    }

    if (step.isLastSection || !step.nextSectionId) {
      // No more sections — course is fully complete
      setIsComplete(true);
      if (onSessionEnd) onSessionEnd({ sessionId, stats: sessionState });
    } else {
      // Advance to the next section automatically
      console.log('[useAdaptiveFlow] Section complete, advancing to:', step.nextSectionId);
      setCurrentSectionId(step.nextSectionId);
      getNextStep(sessionId, step.nextSectionId);
    }
  }, [step?.type, step?.nextSectionId, step?.isLastSection, step?.completedSectionId, sessionId, onSessionEnd, getNextStep, logFlow, autoAdvanceOnSubmit, sessionState]);

  /**
   * Timeline transition logs (high-signal view of flow movement)
   */
  useEffect(() => {
    const previousType = previousStepTypeRef.current;
    const currentType = step?.type || null;

    if (previousType !== currentType) {
      logFlow('timeline.transition', {
        from: previousType,
        to: currentType,
        nextAction: step?.nextAction || null,
        groupSize: step?.groupSize || 0,
        reinforcementCount: step?.reinforcementCount || 0,
        nextSectionId: step?.nextSectionId || null,
      });

      const checks = {
        contentFirstCycle: previousType !== null || currentType === 'content',
        contentToQuestionSet: previousType !== 'content' || currentType === 'question_group',
        questionSetToProgression: previousType !== 'question_group' || currentType === 'section_complete',
        sectionCompleteToContentOrDone:
          previousType !== 'section_complete' || currentType === 'content' || !!isComplete,
        boundedQuestionSet:
          currentType !== 'question_group'
            || ((step?.groupSize || 0) >= 3 && (step?.groupSize || 0) <= 10),
      };

      const pass = Object.values(checks).every(Boolean);
      logFlow('flow_health', {
        pass,
        checks,
        context: {
          from: previousType,
          to: currentType,
          groupSize: step?.groupSize || 0,
          reinforcementCount: step?.reinforcementCount || 0,
          isComplete,
        },
      });

      previousStepTypeRef.current = currentType;
    }
  }, [step?.type, step?.nextAction, step?.groupSize, step?.reinforcementCount, step?.nextSectionId, isComplete, logFlow]);

  /**
   * Auto-prefetch next step while content is displayed
   * This avoids stale question-group prefetch during active answering
   */
  useEffect(() => {
    if (!autoAdvanceOnSubmit) return;

    // Prefetch only from content screens
    if (step?.type !== 'content') return;
    
    // Check if we have all requirements
    if (!sessionId || !currentSectionId) return;
    
    // Skip if already prefetched or in progress
    if (nextStepPrefetched || prefetchInProgress) return;

    console.log('[useAdaptiveFlow] 🚀 Starting auto-prefetch from content');
    logFlow('effect.prefetch_schedule', {
      stepType: step?.type,
      hasContent: !!step?.content,
      sessionId,
      currentSectionId,
    });
    
    // Small delay to let UI render first, then prefetch
    const timer = setTimeout(() => {
      prefetchNextStep();
    }, 150);
    
    return () => clearTimeout(timer);
  }, [step?.type, step?.content, sessionId, currentSectionId, nextStepPrefetched, prefetchInProgress, prefetchNextStep, autoAdvanceOnSubmit, logFlow]);

  // Public API
  return {
    // State
    step,
    sessionId,
    loading,
    error,
    isComplete,
    currentSectionId,
    sessionState,
    answerHistory: answerHistoryRef.current,

    // Actions
    fetchNextStep,
    submitAnswer,
    completeSession,
    resumeToSection,
    getSessionSummary,
    clearError,
    initializeSession,

    // Helpers
    stepType: step?.type,
    isLoading: loading,
    hasError: !!error,
    isQuestion: step?.type === 'question_group',
    isContent: step?.type === 'content',
    isRemediation: step?.type === 'remediation_content',
    isSpacedReview: step?.type === 'spaced_review',
    isSessionComplete: isComplete, // section_complete is handled internally — auto-advances
    
    // Performance indicators (for debugging)
    prefetchReady: !!nextStepPrefetched,
    prefetchInProgress,
  };
}

export default useAdaptiveFlow;
